import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONSULTATION_TYPE = "Complimentary Consultation";

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const onChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: CONSULTATION_TYPE,
        message: form.message || null,
      });
      toast.success("Enquiry received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Please check your details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-32 bg-[#2C2723] text-[#F5ECE1] relative grain"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5 reveal">
            <p className="overline" style={{ color: "#D9C5B2" }}>
              Begin
            </p>
            <h2 className="font-serif-luxe mt-5 text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-[#F5ECE1]">
              A conversation
              <br />
              <em className="text-[#D9C5B2]">before</em> a treatment.
            </h2>
            <p className="mt-8 text-[15px] leading-relaxed text-[#D9C5B2]/80 max-w-md">
              Share a few details and we'll respond personally to arrange your
              private consultation. All enquiries are handled with discretion.
            </p>

            <ul className="mt-12 space-y-5 text-sm text-[#F5ECE1]/90">
              <li className="flex items-start gap-4" data-testid="contact-address">
                <MapPin size={18} className="text-[#D9C5B2] mt-0.5" />
                <a
                  href="https://maps.google.com/?q=312+Burwood+Rd+Belmore+NSW+2192"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#D9C5B2] transition-colors"
                >
                  312 Burwood Road
                  <br />
                  <span className="text-[#D9C5B2]">Belmore, NSW 2192</span>
                </a>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-phone">
                <Phone size={18} className="text-[#D9C5B2] mt-0.5" />
                <a href="tel:+61413431729" className="hover:text-[#D9C5B2] transition-colors">
                  0413 431 729
                </a>
              </li>
              <li className="flex items-start gap-4" data-testid="contact-email">
                <Mail size={18} className="text-[#D9C5B2] mt-0.5" />
                <a
                  href="mailto:info@faceitaesthetics.com.au"
                  className="hover:text-[#D9C5B2] transition-colors break-all"
                >
                  info@faceitaesthetics.com.au
                </a>
              </li>
            </ul>

            <a
              href="https://outlook.office.com/book/FACEITAESTHETICSbyNurseDimi@faceitaesthetics.com.au/?ismsaljsauthenabled"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="external-booking"
              className="mt-12 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#F5ECE1] text-[#2C2723] text-sm tracking-wide hover:bg-[#D9C5B2] transition-colors"
            >
              Book Now via Online Booking <ArrowRight size={14} />
            </a>
          </div>

          <form
            onSubmit={onSubmit}
            data-testid="enquiry-form"
            className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-8 reveal"
            style={{ transitionDelay: "150ms" }}
          >
            <div className="sm:col-span-1">
              <label className="overline" style={{ color: "#D9C5B2" }}>
                Name
              </label>
              <input
                data-testid="input-name"
                type="text"
                value={form.name}
                onChange={(e) => onChange("name", e.target.value)}
                placeholder="Your full name"
                className="input-luxe text-[#F5ECE1] placeholder:text-[#D9C5B2]/40 border-b-[#5C534B] focus:border-b-[#D9C5B2]"
                required
              />
            </div>
            <div className="sm:col-span-1">
              <label className="overline" style={{ color: "#D9C5B2" }}>
                Email
              </label>
              <input
                data-testid="input-email"
                type="email"
                value={form.email}
                onChange={(e) => onChange("email", e.target.value)}
                placeholder="you@email.com"
                className="input-luxe text-[#F5ECE1] placeholder:text-[#D9C5B2]/40 border-b-[#5C534B] focus:border-b-[#D9C5B2]"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="overline" style={{ color: "#D9C5B2" }}>
                Phone
              </label>
              <input
                data-testid="input-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => onChange("phone", e.target.value)}
                placeholder="0400 000 000"
                className="input-luxe text-[#F5ECE1] placeholder:text-[#D9C5B2]/40 border-b-[#5C534B] focus:border-b-[#D9C5B2]"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="overline" style={{ color: "#D9C5B2" }}>
                Message
              </label>
              <textarea
                data-testid="input-message"
                rows={4}
                value={form.message}
                onChange={(e) => onChange("message", e.target.value)}
                placeholder="Tell us a little about your goals (optional)"
                className="input-luxe text-[#F5ECE1] placeholder:text-[#D9C5B2]/40 border-b-[#5C534B] focus:border-b-[#D9C5B2] resize-none"
              />
            </div>
            <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-4 pt-4">
              <p className="text-[11px] tracking-wide text-[#D9C5B2]/60 max-w-sm">
                By submitting, you consent to be contacted about your enquiry.
                We never share your details.
              </p>
              <button
                type="submit"
                data-testid="submit-enquiry"
                disabled={loading}
                className="btn-luxe inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#F5ECE1] text-[#2C2723] text-sm tracking-wide hover:text-white disabled:opacity-60"
              >
                {loading ? "Sending…" : "Submit Enquiry"} <ArrowRight size={14} />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
